import type { GameManual } from '../../types';
import { checklist, h, list, p, tip, warning } from './blocks';

/**
 * Prose-first Barrage core manual (docs/cursor quality bar).
 * Printed company / executive / contract / advanced-tech / round-bonus / objective
 * texts and all expansion rules remain source-blocked — do not invent them.
 */
export {
  barrageScoringRecords,
  filterBarrageScoring,
  validateBarrageScoringTables,
} from './scoringTables';
export type { BarrageScoringRecord } from './scoringTables';

export const barrageManual: GameManual = {
  chapters: [
    {
      title: '1. Cel gry i trzy własności',
      content: [
        warning(
          'Ten podręcznik uczy zweryfikowanych procedur gry podstawowej. Nie zastępuje tekstów wydrukowanych na firmach, dyrektorach, kontraktach, technologiach zaawansowanych, premiach rund ani celach końcowych. Lokalnego źródła zasad dodatków nie ma — nie dopowiadaj ich z pamięci.'
        ),
        h('Czym jest Barrage i po co grasz', 2),
        p(
          'Barrage to pięć rund budowania elektrowni wodnej w Alpach. Stawiasz tamy, kanały i elektrownie, aby przechwytywać płynącą wodę, przesyłać ją do własnej elektrowni i zamieniać w energię. Energia otwiera kontrakty oraz nagrody rundy. Wygrywa gracz z największą liczbą punktów zwycięstwa. Dla początkującego najważniejsze nie jest „postawić dużo elementów”, lecz ułożyć działający łańcuch: skąd woda przychodzi, kto może ją uruchomić i dokąd odpłynie po produkcji.'
        ),
        h('Trzy własności, których nie wolno mieszać', 2),
        p(
          'Woda nie ma właściciela. Własność mają budowle: tamy (podstawa i podwyższenia), kanały oraz elektrownie. Prawo do produkcji zależy jednak od tamy, która zatrzymała kroplę: do swojej produkcji wybierasz wodę stojącą za własną albo neutralną tamą, nigdy za tamą przeciwnika. Kanał przeciwnika wolno wykorzystać w legalnej trasie, ale jego właściciel dostaje za każdą przeprowadzoną kroplę 1 kredyt i 1 VP.'
        ),
        h('Kiedy stosujesz tę zasadę', 2),
        p(
          'Sprawdzaj ją przed każdą produkcją, przed budową „brakującego” elementu sieci oraz gdy ktoś proponuje „wspólną wodę”. Jeśli tama jest cudza, kropla może stać tuż obok twojej elektrowni i nadal nie być twoim legalnym źródłem.'
        ),
        h('Przykład przy stole', 2),
        p(
          'Twoja elektrownia stoi niżej niż neutralna tama z dwoma kroplami. Łączy je kanał rywala. Możesz produkować: tama jest neutralna, elektrownia twoja, a kanał może być cudzy. Po przepuszczeniu obu kropli płacisz rywalowi 2 kredyty, a on zdobywa 2 VP. Gdyby tę samą wodę zatrzymała tama rywala — nawet przy twoim kanale i twojej elektrowni — produkcji nie wolno rozpocząć.'
        ),
        warning(
          'Typowy błąd: „woda jest wspólna, więc każda woda jest dostępna”. Woda jest wspólna, ale tama określa, kto może ją uruchomić.'
        ),
        tip(
          'Wyjątek do zapamiętania: neutralna tama działa jak legalne źródło wody; cudza tama nie. Liczenie produkcji opisuje rozdział 9, a dalszy los użytej kropli — rozdział 11.'
        ),
        h('Podsumowanie', 2),
        p(
          'Cel partii to VP osiągane działającą siecią wody i energii. Budowle mają właściciela; woda nie; prawo produkcji wynika z tamy.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy mogę odczytać efekt nieopisanej tu karty z aplikacji? O: Nie; obowiązuje jej nadruk. P: Czy brak dodatku w menu oznacza, że wolno go ignorować przy stole? O: Nie — oznacza, że zasad dodatku nie zweryfikowano lokalnie. P: Czy mogę produkować przez cudzy kanał? O: Tak, jeśli tama jest twoja lub neutralna, a elektrownia twoja.'
        ),
      ],
    },
    {
      title: '2. Przygotowanie',
      content: [
        h('Co przygotowanie ustala i dlaczego', 2),
        p(
          'Przygotowanie ustala przyszłe źródła wody, neutralne przeszkody, nagrody rund oraz kolejność startową. Zrób je przed pierwszą akcją: pojedyncza neutralna tama z kroplą może być pierwszym legalnym źródłem produkcji, a zablokowane pole akcji nie istnieje w niższym składzie osobowym.'
        ),
        h('Kiedy i kto przygotowuje', 2),
        p(
          'Wszyscy gracze wspólnie rozkładają mapę i tory, potem każdy przygotowuje własną firmę. Dopiero po pełnym setupie odczytujecie kolejność wynikającą z wyboru firmy, dyrektora i kontraktu startowego.'
        ),
        checklist(
          'barrage-setup-shared',
          [
            'Rozłóż mapę, tor energii i planszę zarządzania; wystaw wspólną pulę koparek, betoniarek, kredytów i kropli.',
            'Losowo odkryj 4 kafle źródeł, 5 kafli premii rund i 1 kafel celu końcowego.',
            'Odkryj liczbę kontraktów narodowych równą liczbie graczy minus 1.',
            'Rozdziel prywatne kontrakty na trzy talie i odkryj po 2 z każdej.',
            'Wylosuj po jednym kaflu ustawienia tam neutralnych dla gór, wzgórz i równin; umieść wskazane tamy neutralne poziomów 1, 2 i 3, za każdą po 1 kropli.',
            'W pełnej grze przygotuj Biuro Patentowe: trzy zakryte talie zaawansowanych technologii i trzy odkryte kafle z talii I.',
            'Przy 3 graczach zablokuj pola z symbolem 4; przy 2 graczach także pola z symbolem 3+.',
          ],
          'Setup wspólny'
        ),
        checklist(
          'barrage-setup-player',
          [
            'Każdy gracz otrzymuje firmę, dyrektora, koło budowy, elementy, 12 inżynierów, 5 podstawowych technologii, 6 kredytów, 6 koparek, 4 betoniarki i kontrakt startowy.',
            'Wybór firmy, dyrektora i kontraktu startowego ustala odwrotną kolejność tury.',
            'Ustaw energię na 0 i VP na 10.',
          ],
          'Setup gracza'
        ),
        h('Przykład kontroli gotowości', 2),
        p(
          'Przed pierwszą turą sprawdź trzy rzeczy: wszystkie wymagane neutralne tamy mają kroplę, energia każdego gracza wynosi 0, a pola niedostępne dla aktualnej liczby graczy są zasłonięte. Dopiero wtedy odczytaj kolejność startową. Jeśli w partii trzyosobowej pole „4” nadal leży otwarte, setup nie jest ukończony.'
        ),
        warning(
          'Typowe błędy: brak kropli za neutralną tamą, pozostawienie pola „4” w partii trzech graczy oraz potraktowanie nadrukowanej wartości komponentu jako wartości opisanej w aplikacji.'
        ),
        tip(
          'Wyjątek i odsyłacz: setup mówi, co leży na stole; tekst wybranego komponentu nadal pochodzi z komponentu. Po przygotowaniu przejdź do rozdziału 3 (mapa) i 4 (dochód). Znaczenie neutralnej tamy wraca w rozdziale 9.'
        ),
        h('Podsumowanie', 2),
        p(
          'Gotowa partia ma mapę, źródła, pięć premii, cel, neutralne tamy z kroplami oraz ograniczenia liczby graczy.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy kontrakt narodowy kupuje się jak prywatny? O: Nie; sposób jego realizacji opisuje rozdział 10. P: Czy można pominąć Biuro Patentowe? O: Nie w pełnej konfiguracji podstawowej. P: Od ilu VP zaczynam? O: Od 10.'
        ),
      ],
    },
    {
      title: '3. Mapa i dorzecza',
      content: [
        h('Czym jest mapa', 2),
        p(
          'Mapa to sieć dorzeczy: źródła zasilają rzeki, rzeki prowadzą wodę przez baseny, a baseny łączą miejsca pod tamy, kanały i elektrownie. Tereny dzielą się na góry, wzgórza i równiny — to wpływa na koszt budowy, nie na kierunek spływu. Kierunek zawsze wyznaczają strzałki rzek i basenów.'
        ),
        h('Po co planować łańcuch', 2),
        p(
          'Żaden element samotnie nie daje pełnej produkcji. Tama zatrzymuje wodę, kanał tworzy drogę produkcji między basenem tamy a basenem elektrowni, a elektrownia jest twoim końcem tej drogi. Budujesz więc nie „ładny obiekt”, lecz brakujące ogniwo sieci.'
        ),
        h('Kiedy używać kontroli łańcucha', 2),
        p(
          'Stosuj ją przed budową, przed produkcją oraz przed dodaniem wody. Prześledź kolejno: skąd przyjdzie kropla, która tama ją zatrzyma i czy ma wolną pojemność, jakim kanałem połączysz baseny, czy elektrownia jest twoja, ile zapłacisz za obcy kanał i dokąd kropla popłynie po produkcji.'
        ),
        list([
          'Źródło — miejsce dołożenia kropli w dochodzie rund 1–4 albo przez zarządzanie wodą.',
          'Basen — obszar, w którym stoi tama lub elektrownia; kanał łączy dwa różne baseny.',
          'Tama — zatrzymuje krople do swojej pojemności (poziom).',
          'Kanał — droga produkcji z nadrukowaną wartością.',
          'Elektrownia — twój punkt uruchomienia produkcji.',
        ]),
        h('Pełny przykład planowania', 2),
        p(
          'Masz tanią możliwość budowy kanału, ale technologia i koparki po budowie zostaną na kole. Przed zajęciem pola nie pytaj tylko „czy kanał jest tani?”. Sprawdź, czy po nim masz własną elektrownię, legalną tamę z wodą oraz czy zablokowane maszyny nie są potrzebne do tamy w tej rundzie. Jeżeli na któreś pytanie odpowiedź brzmi „nie”, sama taniość kanału nie tworzy produkcji. Dodatkowo sprawdź, dokąd popłynie woda po użyciu — produkcja może otworzyć miejsce przeciwnikowi niżej na rzece.'
        ),
        warning(
          'Typowy błąd: zbudowanie obiektu bez sprawdzenia pełnej trasy wody albo jej dalszego przepływu.'
        ),
        tip(
          'Wyjątek: cudzy kanał może domknąć trasę, lecz ma koszt dla ciebie i punktuje właściciela. Procedurę budowy przeczytaj w rozdziałach 6–7, a legalność produkcji w rozdziale 9.'
        ),
        h('Podsumowanie', 2),
        p(
          'Mapa to zależność: tama → kanał → własna elektrownia → dalszy przepływ. Strzałki wygrywają z intuicją „w dół mapy”.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy sama tama produkuje? O: Nie. P: Czy kanał może łączyć elementy w jednym basenie? O: Nie. P: Czy muszę znać wszystkie teksty technologii przed pierwszą grą? O: Nie; rozumiej procedurę, a nadruk konkretnej technologii odczytaj z kafla.'
        ),
      ],
    },
    {
      title: '4. Dochód i zasoby',
      content: [
        h('Czym jest dochód', 2),
        p(
          'Dochód to stały dopływ zasobów wynikający z odsłoniętych pól na planszy firmy oraz z kredytów wskazywanych przez tor energii. Nie jest osobną „akcją gracza” w środku tury: odbierasz go w fazie dochodu na początku rundy oraz natychmiast po odsłonięciu pola przez budowę.'
        ),
        h('Po co istnieje', 2),
        p(
          'Dochód finansuje kolejne budowy i akcje zarządzania. Budowa, która odsłania pole dochodu, ma więc podwójny skutek: stawia element na mapie i zwiększa przyszłe wpływy. Maszyny na kole nie są „wydane” — są zablokowane w czasie, więc dochód w maszynach i kredytach planujesz wokół obrotów koła.'
        ),
        h('Kiedy stosujesz', 2),
        p(
          'Na początku każdej rundy wszyscy jednocześnie odbierają odblokowany dochód z plansz firm. Dodatkowo natychmiast po legalnej budowie bierzesz dochód odsłonięty przez zdjęty element. Na końcu rundy kredyty z toru energii także są formą rozliczenia zasobów — opisuje je rozdział 12.'
        ),
        list([
          'Kredyty — płacisz nimi czerwone pola, bank odwrotny nie istnieje jako darmowy zwrot; bank na planszy zarządzania kupuje kredyty za inżynierów.',
          'Koparki — potrzebne głównie do podstaw i kanałów; blokuje je koło.',
          'Betoniarki — potrzebne głównie do podwyższeń i elektrowni; blokuje je koło.',
          'Inżynierowie — wysyłasz ich na pola akcji; wracają na koniec rundy.',
          'Technologie podstawowe — pięć startowych; wracają z koła do otwartego sektora.',
        ]),
        h('Przykład', 2),
        p(
          'Budujesz podstawę i zdejmujesz skrajnie lewy dostępny element z planszy firmy. Pole pod nim pokazuje np. dochód w kredytach lub maszynach — bierzesz go od razu. Od kolejnej fazy dochodu ten wpływ jest już stały, dopóki kolejne budowy nie odsłonią dalszych pól. Jeśli te same maszyny siedzą jeszcze na kole, nie masz ich „w ręku” do kolejnej budowy, mimo że dochód mógł właśnie dołożyć nowe.'
        ),
        warning(
          'Typowy błąd: mylić maszyny na kole z maszynami wydanymi do puli oraz zapominać o natychmiastowym dochodzie po budowie.'
        ),
        tip(
          'Powiązania: budowa i koło — rozdział 6; kredyty z toru energii — rozdział 12; bank i sklep z maszynami — rozdział 8. Konkretne wartości nadrukowane na firmie odczytaj z planszy firmy.'
        ),
        h('Podsumowanie', 2),
        p(
          'Dochód zasila rundę i nagradza odsłonięte pola firmy. Zasoby na kole są niedostępne, dopóki nie wrócą do sektora otwartego.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy dochód po budowie czeka do następnej rundy? O: Nie — bierzesz go natychmiast. P: Czy maszyna na kole liczy się do końcowych VP za zasoby? O: Nie. P: Czy tekst firmy jest w aplikacji? O: Nie; obowiązuje nadruk planszy.'
        ),
      ],
    },
    {
      title: '5. Struktura rundy',
      content: [
        h('Co, kiedy i dlaczego', 2),
        p(
          'Każda z pięciu rund najpierw zasila planszę i graczy, potem pozwala wykonać akcje, a dopiero po nich przesuwa wodę oraz rozlicza wyniki. Ta kolejność wyjaśnia wiele sporów: woda ze źródeł nie spływa swobodnie w środku fazy akcji, chyba że konkretny efekt mówi o kropli płynącej natychmiast.'
        ),
        list([
          'Dochód i źródła: wszyscy odbierają odblokowany dochód; w rundach 1–4 dokładają krople wskazane na kaflach źródeł.',
          'Akcje: według kolejności gracze zajmują po jednym legalnym polu i natychmiast wykonują jego akcję, aż wszyscy spasują.',
          'Przepływ wody: wszystkie krople ze źródeł spływają pojedynczo.',
          'Punktowanie: rozstrzygnij energię, kredyty i aktualną premię rundy.',
          'Koniec rundy: przestaw kolejność, zwróć inżynierów, wyzeruj energię i odśwież technologie. Tę fazę pomija się po rundzie 5.',
        ]),
        h('Przykład pełnego rytmu', 2),
        p(
          'W rundzie 2 odbierasz dochód i dokładasz wskazane krople do źródeł. W fazie akcji budujesz, produkujesz i pasujesz. Następnie krople ze źródeł spływają pojedynczo. Dopiero wtedy porównujecie energię, otrzymujecie kredyty i rozpatrujecie premię rundy; na końcu energia wraca do zera przed kolejną rundą. Jeżeli ktoś wyprodukował w akcji i chce od razu „dostać premię rundy”, jest za wcześnie — najpierw musi dojść wspólny przepływ.'
        ),
        warning(
          'Typowy błąd: rozliczanie premii rundy przed przepływem albo przenoszenie energii do następnej rundy.'
        ),
        tip(
          'Wyjątek: po rundzie 5 nie wykonujesz zwykłego końca rundy; przechodzisz do końcowego punktowania z rozdziału 13. Rozdziały 6–10 opisują, co robisz w fazie akcji.'
        ),
        h('Podsumowanie', 2),
        p(
          'Najpierw dochód i źródła, potem akcje, potem wspólny przepływ, potem punktowanie, potem przygotowanie kolejnej rundy.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy krople źródeł płyną w chwili dołożenia? O: Nie, w fazie przepływu. P: Kiedy odświeżają się technologie? O: W końcu rundy, nie po każdej akcji. P: Czy po spasowaniu jednego gracza faza akcji kończy się? O: Nie; trwa, aż wszyscy spasują.'
        ),
      ],
    },
    {
      title: '6. Budowa i koło konstrukcyjne',
      content: [
        h('Co robi koło i dlaczego', 2),
        p(
          'Budowa nie tylko stawia element. Technologia i maszyny trafiają na koło konstrukcyjne, więc na pewien czas przestają być dostępne. Obrót koła jest ceną czasu: inwestujesz dziś, aby uzyskać element i odsłonić dochód, ale musisz zaplanować, kiedy narzędzia wrócą do otwartego sektora.'
        ),
        h('Kiedy budujesz', 2),
        p(
          'Budowę wykonujesz w fazie akcji, zajmując własne pole konstrukcji. Cztery własne pola zajmuje się od lewej do prawej: wymagają kolejno 1, 2, 3 i 3 inżynierów; czwarte wymaga dodatkowo 3 kredytów. Nie wolno zająć pola, jeśli nie możesz w pełni wykonać budowy.'
        ),
        list([
          'Wybierz budowlę, zajmij pierwsze wolne własne pole konstrukcji i zapłać ewentualne kredyty.',
          'Włóż do otwartego sektora koła właściwą technologię albo technologię uniwersalną oraz wymagane maszyny. Bez obu tych składników budowa jest nielegalna.',
          'Obróć koło dokładnie o 1 sektor; technologia i maszyny pozostają zablokowane, aż wrócą do otwartego sektora.',
          'Weź skrajnie lewy dostępny element danego rodzaju z planszy firmy i umieść go legalnie na mapie.',
          'Natychmiast odbierz dochód odsłonięty przez zdjęty element; od kolejnych faz dochodu będzie on stały.',
        ]),
        checklist(
          'barrage-build-check',
          [
            'Mam technologię i wymagane maszyny w otwartym sektorze.',
            'Obracam koło o dokładnie 1 sektor po włożeniu składników.',
            'Biorę skrajnie lewy dostępny element danego rodzaju.',
            'Miejsce na mapie jest legalne dla tego rodzaju budowli.',
            'Odbieram odsłonięty dochód od razu.',
          ],
          'Kontrola przed/po budowie'
        ),
        h('Przykład jednego obrotu i blokady', 2),
        p(
          'Chcesz postawić podstawę. Zajmujesz najbliższe wolne pole konstrukcji, wkładasz właściwą technologię i wymagane koparki do otwartego sektora, obracasz koło o jeden sektor, pobierasz skrajnie lewy dostępny element podstawy i stawiasz go legalnie. Odsłonięty dochód pobierasz od razu. Koparki nie zostały wydane do puli — są zablokowane. Jeśli w tej samej rundzie chcesz drugi raz użyć tych samych koparek, musisz najpierw obrócić koło (kolejną budową albo warsztatem), aż wrócą do sektora otwartego.'
        ),
        warning(
          'Typowe błędy: postawienie elementu przed obrotem, włożenie samych maszyn bez technologii oraz traktowanie maszyn na kole jak wydanych do puli.'
        ),
        tip(
          'Wyjątki: kredyty wydajesz do puli, nie na koło. Po każdym pojedynczym obrocie natychmiast odzyskujesz to, co wróciło do otwartego sektora. Koszt i legalne miejsce każdego rodzaju budowli opisuje rozdział 7; dodatkowe obroty — warsztat w rozdziale 8.'
        ),
        h('Podsumowanie', 2),
        p(
          'Legalna budowa potrzebuje pola, technologii, maszyn, jednego obrotu i legalnego miejsca. Koło kupuje czas kosztem dostępności narzędzi.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy maszyny znikają po budowie? O: Nie, blokuje je koło. P: Czy mogę odzyskać element z prawej strony planszy firmy? O: Nie, bierzesz skrajnie lewy dostępny element danego rodzaju. P: Czy czwarte pole konstrukcji jest droższe? O: Tak — 3 inżynierów i 3 kredyty.'
        ),
      ],
    },
    {
      title: '7. Cztery budowle',
      content: [
        h('Kiedy budować który element', 2),
        p(
          'Podstawa i podwyższenia zatrzymują wodę, kanał nadaje jej wartość produkcyjną, a elektrownia pozwala ci ją wykorzystać. Buduj z myślą o całym układzie z rozdziału 3, nie jako cztery niezależne zadania.'
        ),
        list([
          'Podstawa tworzy tamę: 3/4/5 koparek odpowiednio na równinach/wzgórzach/górach; pole z czerwoną obwódką kosztuje dodatkowo 3 kredyty. Nie możesz mieć dwóch własnych podstaw w tym samym basenie.',
          'Podwyższenie zwiększa pojemność własnej tamy o 1: 2/3/4 betoniarki na równinach/wzgórzach/górach. Buduje się je na własnej podstawie lub podwyższeniu, najwyżej dwa na jednej podstawie; czerwona obwódka nie dolicza kosztu.',
          'Kanał łączy basen tamy z basenem elektrowni i ma nadrukowaną wartość produkcji. Kosztuje 2 koparki razy tę wartość.',
          'Elektrownia pozwala uruchomić produkcję: koszt to 2 betoniarki plus 1 za każdą twoją wcześniej zbudowaną elektrownię; czerwone pole kosztuje dodatkowo 3 kredyty. Nie możesz mieć dwóch własnych elektrowni w tym samym basenie.',
        ]),
        tip(
          'Poziom tamy jest jej pojemnością: podstawa = poziom 1, maksymalnie dwa podwyższenia = poziom 3.'
        ),
        h('Przykład decyzji przy stole', 2),
        p(
          'Masz podstawę poziomu 1, więc zatrzyma ona tylko jedną kroplę. Podwyższenie kosztuje betoniarki zależnie od terenu i zwiększa pojemność o 1. Kanał o wartości 3 będzie kosztował 6 koparek, ale każda użyta nim kropla wniesie do obliczenia wartość 3. Elektrownia jest potrzebna, by w ogóle produkować, a każda kolejna twoja elektrownia podnosi jej koszt. Jeśli budujesz kanał „na zapas” bez legalnej tamy i elektrowni, płacisz koszt bez natychmiastowej produkcji.'
        ),
        warning(
          'Typowe błędy: traktowanie wartości kanału jako liczby kropli, budowanie dwóch własnych podstaw lub elektrowni w jednym basenie oraz doliczanie czerwonego kosztu do podwyższenia.'
        ),
        tip(
          'Wyjątki: tylko podstawa i elektrownia na czerwonym polu płacą dodatkowe 3 kredyty; podwyższenie nie. Pojemność tamy rozstrzyga rozdział 11, a wartość kanału działa w wzorze produkcji z rozdziału 9.'
        ),
        h('Podsumowanie', 2),
        p(
          'Tama przechowuje, kanał mnoży, elektrownia produkuje. Koszt terenu i czerwone obwódki zmieniają cenę, nie regułę własności.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy podwyższenie mogę położyć na neutralnej tamie? O: Nie; buduje się je na własnej podstawie lub podwyższeniu. P: Czy kanał może łączyć elementy w jednym basenie? O: Nie. P: Ile podwyższeń maksymalnie na jednej podstawie? O: Dwa.'
        ),
      ],
    },
    {
      title: '8. Pola zarządzania',
      content: [
        h('Co robi akcja i kiedy jest legalna', 2),
        p(
          'W fazie akcji wysyłasz inżynierów na własne pola konstrukcji albo wspólne pola zarządzania. Zajęcie wspólnego pola zwykle zamyka je do końca rundy, więc najpierw sprawdź pełny efekt. Nie wolno wysłać inżynierów na pole, którego efektu nie potrafisz w pełni wykonać.'
        ),
        list([
          'Stacja turbin: produkuj energię, stosując premię albo karę nadrukowaną na zajętym polu.',
          'Zarządzanie wodą: jedno pole dokłada do dwóch kropli na wybrane źródła na późniejszy przepływ; drugie dokłada jedną kroplę, która płynie natychmiast.',
          'Bank: za dowolną liczbę wysłanych inżynierów weź tyle samo kredytów. To jedyne pole nieograniczone — można z niego korzystać mimo obecności innych.',
          'Warsztat: obróć koło o podaną liczbę sektorów, płacąc ewentualny koszt; po każdym pojedynczym obrocie odzyskaj to, co wróciło do sektora otwartego.',
          'Sklep z maszynami: zapłać kredyty z lewej strony symbolu i weź wskazane po prawej maszyny.',
          'Biuro kontraktów: bezpłatnie weź 1 odkryty prywatny kontrakt albo za 1 kredyt weź 2. Uzupełnij odkryte karty dopiero na końcu swojej tury i zatrzymaj najwyżej 3 niespełnione.',
          'Biuro Patentowe: zapłać 5 kredytów za wskazaną odkrytą zaawansowaną technologię; jest dostępna od razu, a uzupełnienie następuje dopiero na koniec rundy.',
        ]),
        h('Przykład wyboru akcji', 2),
        p(
          'Chcesz odzyskać koparki z koła, więc zajmujesz warsztat i wykonujesz wskazane obroty pojedynczo. Po każdym obrocie sprawdzasz otwarty sektor i natychmiast odzyskujesz zwrócone składniki. Jeśli zamiast tego bierzesz dwa prywatne kontrakty, płacisz 1 kredyt, a ich odkryte miejsce uzupełniasz dopiero po zakończeniu swojej tury — nie w trakcie wyboru drugiej karty.'
        ),
        warning(
          'Typowe błędy: potraktowanie banku jak zablokowanego pola, uzupełnianie kontraktów w środku wyboru oraz położenie inżynierów na akcji bez możliwości wykonania efektu.'
        ),
        tip(
          'Wyjątki: bank jest jedynym nieograniczonym polem. Kropla „natychmiast” rozpatruje przepływ od razu (rozdział 11), a stacja turbin uruchamia procedurę z rozdziału 9. Nadruk technologii zaawansowanej pozostaje źródłem jej konkretnego efektu.'
        ),
        h('Podsumowanie', 2),
        p(
          'Pole zarządzania zmienia zasoby, wodę, koło, kontrakty albo produkcję. Najpierw sprawdź pełną wykonalność, potem zajmuj pole.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy mogę mieć cztery niespełnione prywatne kontrakty? O: Nie, limit wynosi trzy. P: Kiedy uzupełniam technologię zaawansowaną? O: Dopiero na końcu rundy. P: Czy bank zamyka się po pierwszym użyciu? O: Nie.'
        ),
      ],
    },
    {
      title: '9. Produkcja energii',
      content: [
        h('Co musi istnieć przed produkcją', 2),
        p(
          'Produkcja nie jest wydaniem dowolnej kropli. Potrzebujesz własnej elektrowni, własnej albo neutralnej tamy z co najmniej jedną kroplą oraz kanału łączącego dokładnie ich baseny. To sprawdzenie wykonaj przed wyborem liczby kropli i przed zajęciem pola turbiny.'
        ),
        list([
          'Wybierz własną albo neutralną tamę zawierającą co najmniej 1 kroplę, własną elektrownię oraz kanał łączący dokładnie te dwa baseny.',
          'Wybierz dowolną liczbę kropli z tej tamy i przesuń je przez kanał do elektrowni.',
          'Energia z działania = liczba kropli × wartość kanału, następnie zastosuj premię/karę pola turbiny. Wynik musi być co najmniej 1.',
          'Przesuń znacznik energii o wynik. Po przekroczeniu 30 odwróć znacznik i kontynuuj od początku; do premii/dochodu traktuj go jak stojący na 30.',
          'Po produkcji każda użyta kropla dalej spływa od basenu elektrowni według zwykłych zasad przepływu.',
        ]),
        checklist(
          'barrage-produce-check',
          [
            'Tama jest moja albo neutralna i ma krople.',
            'Elektrownia jest moja.',
            'Kanał łączy dokładnie oba baseny.',
            'Policzę krople × wartość kanału, potem modyfikator pola.',
            'Zapłacę za cudzy kanał (1 kredyt i 1 VP właścicielowi za każdą kroplę).',
            'Po produkcji puszczam użyte krople dalej.',
          ],
          'Kontrola legalnej produkcji'
        ),
        h('Przykład pełnej produkcji przez cudzy kanał', 2),
        p(
          'Neutralna tama ma 2 krople. Twoją elektrownię łączy z jej basenem kanał przeciwnika o wartości 4, a zajęte pole turbiny daje +1. Przepuszczasz obie krople: 2 × 4 + 1 = 9 energii. Płacisz właścicielowi kanału 2 kredyty, a on zdobywa 2 VP. Każda użyta kropla następnie płynie dalej od basenu twojej elektrowni — nie zostaje „zużyta i usunięta” przy turbinie.'
        ),
        h('Kontrprzykład: cudza tama', 2),
        p(
          'Ta sama sieć, ale wodę zatrzymała tama przeciwnika. Masz własną elektrownię i nawet własny kanał. Produkcja jest nielegalna: tama źródłowa nie jest twoja ani neutralna. Nie „wynajmujesz” cudzej tamy przy produkcji.'
        ),
        warning(
          'Typowe błędy: użycie wody za cudzą tamą, pominięcie opłaty i punktów za cudzy kanał, sumowanie produkcji z różnych akcji do jednego kontraktu oraz zostawienie użytej wody przy elektrowni.'
        ),
        tip(
          'Wyjątki: wynik po modyfikatorze musi wynosić co najmniej 1. Kanał może być cudzy, lecz tama nie. Po przekroczeniu 30 tor nadal daje dla premii i dochodu wynik 30. Kontrakty rozdziela rozdział 10, a dalszy przepływ rozdział 11.'
        ),
        h('Podsumowanie', 2),
        p(
          'Wzór to krople × wartość kanału, potem modyfikator pola. Własność tamy i elektrowni jest twardym warunkiem; kanał może być obcy.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy własna elektrownia jest wymagana? O: Tak. P: Czy mogę przepuścić tylko jedną kroplę z pełnej tamy? O: Tak. P: Czy energia z toru = wynik kontraktu? O: Nie; kontrakt patrzy na wynik jednej produkcji.'
        ),
      ],
    },
    {
      title: '10. Kontrakty',
      content: [
        h('Co, kiedy i dlaczego', 2),
        p(
          'Bezpośrednio po produkcji możesz spełnić dokładnie jeden kontrakt. Kontrakt zamienia wystarczająco wysoką pojedynczą produkcję w nadrukowaną nagrodę. Nie jest dodatkową akcją i nie porównuje się go z całym torem energii ani z sumą kilku produkcji.'
        ),
        list([
          'Zakończ legalną produkcję i zapamiętaj energię uzyskaną w tej właśnie akcji.',
          'Wybierz jeden prywatny albo, gdy osiągasz jego próg, odkryty narodowy kontrakt.',
          'Porównaj próg z wynikiem jednej produkcji.',
          'Odbierz dokładnie nadrukowaną nagrodę i odwróć spełniony kontrakt; nie spełnisz go ponownie.',
        ]),
        h('Przykład granicy jednej produkcji', 2),
        p(
          'Wynik 7 może spełnić kontrakt o progu 3 albo 7, ale nie oba naraz. Dwie osobne produkcje po 4 nie spełniają kontraktu o progu 7, nawet jeśli znacznik energii w rundzie wzrósł o 8. Kontrakt narodowy działa zamiast prywatnego; pierwszy gracz, który go spełni, zabiera i odwraca go przed sobą.'
        ),
        h('Przykład kilku kontraktów w rundzie', 2),
        p(
          'W tej samej rundzie produkujesz raz na 5 i spełniasz prywatny kontrakt o progu 5. Później zajmujesz inne pole turbiny, produkujesz na 8 i spełniasz drugi kontrakt (prywatny albo narodowy, jeśli próg jest osiągnięty). To jest legalna „realizacja kilku kontraktów”: każdy po osobnej produkcji. Nadal nie wolno po jednej produkcji odwrócić dwóch kart.'
        ),
        warning(
          'Typowe błędy: łączenie wyników kilku produkcji, spełnianie dwóch kontraktów po jednej produkcji oraz próba kupienia kontraktu narodowego w Biurze Kontraktów.'
        ),
        tip(
          'Wyjątek: treść nagrody należy odczytać z fizycznego kontraktu; aplikacja nie zastępuje katalogu jego efektów. Prywatne kontrakty bierzesz akcją z rozdziału 8, a poprawne liczenie wyniku opisuje rozdział 9.'
        ),
        h('Podsumowanie', 2),
        p(
          'Jeden wynik produkcji → najwyżej jeden kontrakt. Wiele kontraktów wymaga wielu osobnych produkcji.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy energia pozostała na torze pomaga później spełnić kontrakt? O: Nie. P: Czy odwrócony kontrakt wraca do gry? O: Nie. P: Czy narodowy bierze się z Biura Kontraktów? O: Nie.'
        ),
      ],
    },
    {
      title: '11. Przepływ wody',
      content: [
        h('Co robi przepływ i kiedy go rozstrzygać', 2),
        p(
          'Woda spływa w fazie przepływu, po produkcji oraz po efekcie dodającym kroplę „natychmiast”. W każdym przypadku rozstrzygasz ją pojedynczo, zgodnie ze strzałkami rzek i basenów. To ważne, bo wcześniejsza kropla może zapełnić tamę, którą późniejsza już ominie.'
        ),
        list([
          'Podczas przepływu zdejmuj krople ze źródeł i przemieszczaj je pojedynczo zgodnie ze strzałkami rzek oraz basenów.',
          'Gdy kropla dochodzi do tamy, zatrzymuje się tylko wtedy, gdy tama ma wolną pojemność. Pojemność równa się poziomowi tamy.',
          'Pełna tama nie zatrzymuje kolejnej kropli: kropla przelewa się i płynie dalej, aż zatrzyma ją następna niepełna tama lub opuści mapę przez dolny basen.',
          'Ta sama procedura działa dla kropli dodanej „natychmiast” oraz dla kropli wychodzącej po produkcji.',
        ]),
        checklist(
          'barrage-flow-check',
          [
            'Przesuwam krople pojedynczo, nie grupą.',
            'Zatrzymuję tylko na niepełnej tamie.',
            'Pełna tama zawsze przepuszcza kolejną kroplę.',
            'Po produkcji nie zostawiam kropli przy elektrowni.',
          ],
          'Kontrola przepływu'
        ),
        h('Przykład przepełnienia', 2),
        p(
          'Pełna tama poziomu 1 w górach przepuszcza dwie kolejne krople. Niżej tama poziomu 3 ma jedno wolne miejsce: pierwsza kropla ją wypełnia, druga przelewa się poza mapę. Gracz nie wybiera, czy pełna tama „przyjmie jeszcze jedną” — nie przyjmie.'
        ),
        h('Przykład przepływu przez kilka basenów', 2),
        p(
          'Pięć kropli spływa z jednego źródła. Prowadź każdą osobno: pełna tama górska przepuszcza pierwsze dwie, kolejna niepełna tama poziomu 2 zatrzymuje następną, czwarta kropla wypełnia dalszą tamę poziomu 1, a piąta płynie dalej do kolejnego wolnego miejsca albo poza mapę. Kolejność kropli zmienia, kto ostatecznie dostanie wodę — dlatego nie przesuwaj „wszystkich pięciu naraz”.'
        ),
        warning(
          'Typowe błędy: zatrzymanie kropli na pełnej tamie, przesuwanie całej grupy bez rozstrzygania po kolei i pozostawienie użytej do produkcji kropli przy elektrowni.'
        ),
        tip(
          'Wyjątki: pełna tama zawsze przepuszcza kroplę. Pojemność podstawy, podwyższeń i poziomów opisuje rozdział 7. To, kto może produkować z zatrzymanej kropli, rozstrzyga rozdział 1.'
        ),
        h('Podsumowanie', 2),
        p(
          'Jedna kropla płynie aż zatrzyma ją niepełna tama lub opuści mapę. Kolejność rozstrzygnięć jest częścią zasady.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy dodana natychmiast kropla czeka na fazę przepływu? O: Nie. P: Czy poziom 3 zatrzyma trzy krople? O: Tak, jeżeli ma trzy wolne miejsca. P: Czy mogę zatrzymać kroplę „w połowie kanału”? O: Nie — kanał służy produkcji, nie magazynowaniu.'
        ),
      ],
    },
    {
      title: '12. Punktowanie rundy',
      content: [
        h('Co rozliczasz i dlaczego', 2),
        p(
          'Po przepływie nagradzacie energię, pobieracie kredyty, sprawdzacie premię rundy i ustawiając kolejność przygotowujecie następną walkę o pola akcji. Najmniejsza produkcja daje pierwszeństwo, więc wysoka energia nie zawsze oznacza najlepszą pozycję w kolejnej rundzie.'
        ),
        list([
          'Tylko gracze z co najmniej 1 energią rywalizują o premię energii: pierwszy dostaje 6 VP, drugi 2 VP.',
          'Przy remisie na pierwszym miejscu zsumuj 6 i 2 VP, podziel między remisujących i zaokrąglij w górę; nie przyznawaj wtedy drugiego miejsca. Przy remisie na drugim każdy remisujący dostaje 1 VP.',
          'Każdy bierze kredyty wskazane przez osiągnięte pole toru. Na 0 bierze 3 kredyty i traci 3 VP.',
          'Rozpatrz bieżący kafel premii: minimum 6 energii jest konieczne. W odpowiedniej lub dalszej sekcji toru dostajesz pełną nagrodę, w każdej wcześniejszej obniż ją o 4 VP; premia nie może dać ujemnych VP.',
          'Usuń kafel premii. W końcu rundy najmniejsza produkcja daje pierwszeństwo; przy remisie odwróć wzajemną kolejność remisujących z poprzedniej rundy. Zwróć inżynierów, wyzeruj energię i odśwież technologie.',
        ]),
        checklist(
          'barrage-round-end-check',
          [
            'Najpierw był przepływ źródeł, potem punktowanie.',
            'Do nagrody energii liczą się tylko gracze z ≥1 energii.',
            'Remis na 1. miejscu dzieli łącznie 8 VP w górę; bez 2. miejsca.',
            'Energia wraca do 0 przed następną rundą (poza przejściem do końca gry po rundzie 5).',
          ],
          'Kontrola końca rundy'
        ),
        h('Przykład remisu na torze energii', 2),
        p(
          'Dwóch graczy remisuje na prowadzeniu energii. Sumujesz nagrody pierwszego i drugiego miejsca: 6 + 2 = 8 VP. Każdy dostaje sufit z 8 / 2, czyli 4 VP; nie przyznajesz już nagrody za drugie miejsce. Gdy remis dotyczy drugiego miejsca, każdy remisujący dostaje 1 VP. Gracz z 0 energii w ogóle nie wchodzi do tej rywalizacji — zamiast tego bierze 3 kredyty i traci 3 VP.'
        ),
        warning(
          'Typowe błędy: dopuszczenie do nagrody energii gracza z 0, przyznanie drugiego miejsca po remisie na pierwszym oraz pozostawienie energii i inżynierów na planszy na następną rundę.'
        ),
        tip(
          'Wyjątki: nagroda premii rundy wymaga co najmniej 6 energii; jej konkretny tekst jest na kaflu. Na 0 energii pobierasz 3 kredyty i tracisz 3 VP. Po rundzie 5 zamiast zwykłego przygotowania przechodzisz do rozdziału 13.'
        ),
        h('Podsumowanie', 2),
        p(
          'Rozlicz energię, kredyty i premię, potem daj pierwszeństwo najniższej produkcji. Remisy mają własne, ścisłe reguły dzielenia VP.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy remisujący liderzy dzielą 6 VP? O: Dzielą łącznie 8 VP. P: Czy premia rundy może dać wynik ujemny? O: Nie. P: Co przy remisie kolejności? O: Odwróć wzajemną kolejność remisujących z poprzedniej rundy.'
        ),
      ],
    },
    {
      title: '13. Koniec gry i remisy',
      content: [
        h('Kiedy i co punktujesz', 2),
        p(
          'Po punktowaniu rundy 5 kończy się zwykły rytm rund. Rozstrzygasz kafel celu końcowego, pozostałe zasoby i wodę za własnymi tamami. To podsumowanie stanu końcowego, nie nowa faza budowania ani produkcji.'
        ),
        list([
          'Po punktowaniu rundy 5 uporządkuj graczy według warunku kafla celu. Miejsca 1/2/3 dostają 15/10/5 VP; w remisie podziel odpowiednie progi i zaokrąglij w górę.',
          'Dodaj 1 VP za każde pełne 5 dostępnych kredytów, koparek i betoniarek łącznie. Maszyny zablokowane na kole nie liczą się.',
          'Dodaj 1 VP za każdą kroplę zatrzymaną za twoimi tamami.',
          'Najwięcej VP wygrywa. W remisie wygrywa gracz, który wyprodukował więcej energii w rundzie 5.',
        ]),
        checklist(
          'barrage-endgame-check',
          [
            'Nie wykonuję już zwykłego przygotowania kolejnej rundy.',
            'Cel końcowy: 15/10/5 VP za miejsca 1/2/3 (remis dzielony w górę).',
            'Zasoby: tylko dostępne; koło nie liczy się.',
            'Woda: tylko za moimi tamami.',
            'Remis VP → porównaj energię z rundy 5.',
          ],
          'Kontrola końca gry'
        ),
        h('Przykład zamknięcia', 2),
        p(
          'Masz łącznie 10 dostępnych kredytów, koparek i betoniarek, więc dostajesz 2 VP. Maszyny nadal na kole nie zwiększają tej liczby. Jeśli za twoimi tamami leżą trzy krople, dodajesz kolejne 3 VP. Następnie porównujesz pełne sumy VP; przy remisie nie cofaj się do całej partii, lecz porównaj energię wyprodukowaną w rundzie 5.'
        ),
        warning(
          'Typowe błędy: liczenie maszyn zablokowanych na kole, liczenie kropli za neutralnymi lub cudzymi tamami oraz zastosowanie zasad końca rundy po rundzie 5.'
        ),
        tip(
          'Wyjątek: konkretne kryterium celu końcowego odczytaj z kafla, bo nie jest katalogowane w aplikacji. Zasoby wracające z koła i ich dostępność wyjaśnia rozdział 6.'
        ),
        h('Podsumowanie', 2),
        p(
          'Końcowy wynik łączy cel, dostępne zasoby i wodę za twoimi tamami. Remis VP rozstrzyga energia z rundy 5.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy maszyna na kole jest dostępna? O: Nie. P: Co rozstrzyga remis VP? O: Więcej energii wyprodukowanej w rundzie 5. P: Czy krople za neutralną tamą dają mi VP? O: Nie.'
        ),
      ],
    },
    {
      title: '14. Pierwsza pełna runda',
      content: [
        h('Dlaczego warto przejść ten przykład', 2),
        p(
          'Poniższy przebieg nie dodaje nowych zasad. Pokazuje momenty kontroli, które chronią przed najczęstszymi błędami: budową bez planu, nielegalnym użyciem wody i pomyleniem przepływu z punktowaniem.'
        ),
        list([
          'Odbierz dochód z odsłoniętych pól firmy; w rundzie 1–4 dołóż wskazane krople na wszystkie źródła.',
          'W akcji budowy zajmij legalne pole, włóż technologię i wymagane maszyny do otwartego sektora, obróć koło o 1, postaw legalny element i odbierz odsłonięty dochód.',
          'Przed produkcją sprawdź: własna/neutralna tama z wodą, własna elektrownia, łączący je kanał oraz koszt obcego kanału. Przeprowadź krople, policz energię i ewentualnie spełnij jeden kontrakt.',
          'Gdy wszyscy spasują, spuszczaj krople ze źródeł pojedynczo, zatrzymując je wyłącznie na niepełnych tamach.',
          'Rozdaj VP za energię, kredyty i premię rundy. Ustal kolejność od najmniejszej produkcji, zwróć inżynierów, wyzeruj energię i odśwież technologie.',
        ]),
        h('Kontrola po przykładzie', 2),
        p(
          'Jeżeli po każdym kroku umiesz wskazać, kto jest właścicielem budowli, gdzie dokładnie leży każda kropla i które maszyny są w otwartym sektorze, rozumiesz rdzeń pierwszej partii. Jeśli któregoś z tych trzech faktów nie potrafisz wskazać, wróć do odpowiedniego rozdziału przed kolejną rundą.'
        ),
        warning(
          'Typowy błąd: po produkcji od razu punktować rundę. Najpierw musi nastąpić wspólny przepływ źródeł.'
        ),
        tip(
          'Odsyłacze: gdy przykład zatrzyma się na budowie, wróć do rozdziału 6 albo 7; przy wodzie do 9 albo 11; przy rozliczeniu do 12; przy dochodzie do 4.'
        ),
        h('Podsumowanie', 2),
        p(
          'Runda to dochód → akcje → przepływ → punktowanie → przygotowanie.'
        ),
        h('Mikro-FAQ', 2),
        p(
          'P: Czy po spasowaniu jednego gracza faza akcji kończy się natychmiast? O: Nie; trwa, aż wszyscy spasują. P: Czy konstrukcja może od razu odsłonić dochód? O: Tak, odbierasz go natychmiast.'
        ),
      ],
    },
    {
      title: '15. Błędy, wyjątki i granica źródeł',
      content: [
        h('Szybkie odpowiedzi dla stołu', 2),
        p(
          'Czy kanał łączy elementy w tym samym basenie? Nie; łączy basen źródłowej tamy z basenem docelowej elektrowni. Czy po użyciu woda pozostaje przy elektrowni? Nie; płynie dalej. Czy wolno wysłać inżynierów bez możliwości wykonania efektu? Nie. Czy energia z toru spełnia kontrakt? Nie; liczy się wynik jednej konkretnej produkcji. Czy mogę produkować z wody za cudzą tamą? Nie.'
        ),
        h('Trzy pytania przed każdą produkcją', 2),
        list([
          'Czy wybrana tama jest moja albo neutralna i ma krople?',
          'Czy elektrownia jest moja, a kanał łączy dokładnie oba baseny?',
          'Czy po obliczeniu zapłaciłem za cudzy kanał, rozliczyłem tylko jeden kontrakt i puściłem krople dalej?',
        ]),
        h('Trzy pytania po każdej budowie', 2),
        list([
          'Czy użyłem technologii i maszyn w otwartym sektorze oraz obróciłem koło?',
          'Czy wziąłem skrajnie lewy dostępny element i postawiłem go w legalnym miejscu?',
          'Czy wiem, które składniki są teraz zablokowane, a jaki dochód odsłoniłem?',
        ]),
        warning(
          'Granica kompletności: brak lokalnego materiału dla dodatków. Nie oznaczono żadnego dodatku jako gotowego do gry z aplikacji. Do niezależnego od papierowych komponentów katalogu nadal brakuje tekstów i wartości firm, dyrektorów, prywatnych i narodowych kontraktów, technologii zaawansowanych, premii rund i celów.'
        ),
        tip(
          'Ta instrukcja jest zakończonym tekstem nauczania procedur podstawy w granicy dostępnych źródeł. Status treści pozostaje partial, ponieważ zielony test nie weryfikuje brakujących nadrukowanych efektów ani zasad dodatków.'
        ),
        h('Końcowe podsumowanie', 2),
        p(
          'Najpierw zaplanuj trasę wody, potem buduj z uwzględnieniem koła i dochodu, produkuj wyłącznie przez legalny układ, przesuń użyte krople dalej i rozlicz rundę we właściwej kolejności. P: Czy ta instrukcja pozwala zastąpić tekst dowolnego kafla? O: Nie. P: Czy można uznać dodatki za opisane? O: Nie, bez lokalnego źródła byłoby to wymyślaniem zasad.'
        ),
      ],
    },
  ],
  firstGameSteps: [
    {
      id: 1,
      title: 'Rozłóż źródła i cele',
      content: [
        'Wykonaj wszystkie kroki przygotowania, w tym tamy neutralne z kroplami.',
        'Ustaw energię na 0, VP na 10 i zablokuj pola zależne od liczby graczy.',
      ],
      why: 'Woda i dostępne pola definiują tempo całej partii.',
      commonMistake: 'Nie pomijaj tam neutralnych ani kropli stojących za nimi.',
      chapterIndex: 1,
    },
    {
      id: 2,
      title: 'Zrozum dochód i koło',
      content: [
        'Odbierz startowy dochód tylko z odsłoniętych pól.',
        'Zapamiętaj: maszyny na kole są zablokowane, nie wydane.',
      ],
      why: 'Bez tego łatwo „zgubić” zasoby po pierwszej budowie.',
      commonMistake: 'Nie traktuj maszyn na kole jak usuniętych z gry.',
      chapterIndex: 3,
    },
    {
      id: 3,
      title: 'Zbuduj pierwszy element',
      content: [
        'Zajmij pierwsze wolne pole konstrukcji.',
        'Włóż technologię i maszyny do otwartego sektora.',
        'Obróć koło, postaw element i odbierz odsłonięty dochód.',
      ],
      why: 'Pokazuje pełny koszt budowy: zasoby + czas na kole.',
      commonMistake: 'Nie buduj elementu bez technologii albo przed obrotem koła.',
      chapterIndex: 5,
    },
    {
      id: 4,
      title: 'Wytwórz energię legalnie',
      content: [
        'Wskaż własną/neutralną tamę z wodą, własną elektrownię i łączący kanał.',
        'Policz krople × wartość kanału plus modyfikator pola.',
        'Zapłać za cudzy kanał, jeśli go używasz, i puść krople dalej.',
      ],
      why: 'To klucz do kontraktów i punktacji rundy.',
      commonMistake: 'Woda za cudzą tamą nie jest legalnym źródłem twojej produkcji.',
      chapterIndex: 8,
    },
    {
      id: 5,
      title: 'Rozstrzygnij przepływ i zamknij rundę',
      content: [
        'Przesuwaj krople pojedynczo; pełna tama zawsze przepuszcza.',
        'Przyznaj energię, kredyty i premię rundy.',
        'Ustal kolejność od najmniejszej produkcji; zwróć inżynierów i wyzeruj energię.',
      ],
      why: 'Kolejność faz chroni przed fałszywym punktowaniem w środku akcji.',
      commonMistake: 'Nie punktuj rundy przed wspólnym przepływem źródeł.',
      chapterIndex: 11,
    },
  ],
};
